import { useState, useEffect, useMemo } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Grid, MenuItem, FormControlLabel, Checkbox, Alert,
} from "@mui/material";
import { getAvailableRooms, createReservation } from "./ReservationApi";

const EMPTY_FORM = {
    phone: "", fname: "", lname: "", email: "",
    requestedCheckin: "", requestedCheckout: "",
    adults: 1, children: 0, hasPets: false,
    rentalType: "daily", rate: "", tenancyDescription: "",
    roomId: "",
};

export default function ReservationFormDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [availableRooms, setAvailableRooms] = useState([]);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [touched, setTouched] = useState(false);

    const dateRangeValid = useMemo(
        () => form.requestedCheckin && form.requestedCheckout && form.requestedCheckout > form.requestedCheckin,
        [form.requestedCheckin, form.requestedCheckout]
    );

    useEffect(() => {
        if (!open) return;
        setForm(EMPTY_FORM);
        setError(null);
        setTouched(false);
    }, [open]);

    useEffect(() => {
        if (!dateRangeValid) {
            setAvailableRooms([]);
            return;
        }
        getAvailableRooms(form.requestedCheckin, form.requestedCheckout)
            .then(setAvailableRooms)
            .catch(() => setAvailableRooms([]));
    }, [form.requestedCheckin, form.requestedCheckout, dateRangeValid]);

    function update(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
    }

    async function handleSubmit() {
        setTouched(true);
        setError(null);
        if (!form.phone.trim() || !form.fname.trim() || !dateRangeValid) return;

        setSubmitting(true);
        try {
            const reservation = await createReservation({
                ...form,
                roomId: form.roomId || null,
                rate: form.rate ? Number(form.rate) : null,
                adults: Number(form.adults),
                children: Number(form.children),
                hasPets: form.hasPets ? 1 : 0,
            });
            onCreated?.(reservation);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>New reservation</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                        <TextField
                            label="Phone"
                            fullWidth
                            value={form.phone}
                            onChange={(e) => update("phone", e.target.value)}
                            // error={touched && !form.phone.trim()}
                            // helperText={touched && !form.phone.trim() ? "Required — used to find or create the guest record" : " "}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Email (optional)"
                            fullWidth
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="First name"
                            fullWidth
                            value={form.fname}
                            onChange={(e) => update("fname", e.target.value)}
                            // error={touched && !form.fname.trim()}
                            // helperText={touched && !form.fname.trim() ? "Required" : " "}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Last name"
                            fullWidth
                            value={form.lname}
                            onChange={(e) => update("lname", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            label="Check-in" type="date" fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={form.requestedCheckin}
                            onChange={(e) => update("requestedCheckin", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Check-out" type="date" fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={form.requestedCheckout}
                            onChange={(e) => update("requestedCheckout", e.target.value)}
                            // error={touched && !dateRangeValid}
                            // helperText={touched && !dateRangeValid ? "Must be after check-in" : " "}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <TextField
                            label="Adults" type="number" fullWidth
                            inputProps={{ min: 1 }}
                            value={form.adults}
                            onChange={(e) => update("adults", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            label="Children" type="number" fullWidth
                            inputProps={{ min: 0 }}
                            value={form.children}
                            onChange={(e) => update("children", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            select label="Rental type" fullWidth
                            value={form.rentalType}
                            onChange={(e) => update("rentalType", e.target.value)}
                        >
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            label="Rate ($)" type="number" fullWidth
                            value={form.rate}
                            onChange={(e) => update("rate", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                            control={<Checkbox checked={form.hasPets} onChange={(e) => update("hasPets", e.target.checked)} />}
                            label="Traveling with pets"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            select label="Room (optional — can assign later or at check-in)" fullWidth
                            value={form.roomId}
                            onChange={(e) => update("roomId", e.target.value)}
                            disabled={!dateRangeValid}
                            // helperText={
                            //     dateRangeValid
                            //         ? `${availableRooms.length} room(s) available for these dates`
                            //         : "Enter valid dates to see available rooms"
                            // }
                        >
                            <MenuItem value="">Unassigned</MenuItem>
                            {availableRooms.map((r) => (
                                <MenuItem key={r.id} value={r.id}>
                                    Room {r.alias} — {r.bedType}, {r.size}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Notes" fullWidth multiline rows={2}
                            value={form.tenancyDescription}
                            onChange={(e) => update("tenancyDescription", e.target.value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? "Creating…" : "Create reservation"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}